<script lang="ts" setup>
  import { Info } from 'lucide-vue-next'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip'
  import { resolveCloGainOutput, type CloGainOutput } from '~/helpers/clo-gain-output'
  import { formatISOToDate } from '~/utils/date'
  import type { AssetState } from '~/types/asset'
  import type { ConfigurationValues } from '~/types/asset-configuration'

  const props = defineProps<{
    states: AssetState[]
    values: ConfigurationValues
  }>()

  const { t, locale } = useI18n()

  const output = computed<CloGainOutput>(() => resolveCloGainOutput(props.states, props.values))

  const formatPercent = (value: number): string =>
    `${new Intl.NumberFormat(locale.value, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value)} %`

  const formatHours = (value: number): string =>
    `${new Intl.NumberFormat(locale.value, { maximumFractionDigits: 0 }).format(value)} h`

  const details = computed<string>(() => {
    const current = output.value
    if (current.status !== 'adjusted') return ''
    const parts = [
      current.ratedHours === null
        ? formatHours(current.burnHours)
        : t('assets.configuration.cloGain.burnHoursOfRated', {
            burnHours: formatHours(current.burnHours),
            ratedHours: formatHours(current.ratedHours)
          })
    ]
    if (current.adjustedAt) parts.push(formatISOToDate(current.adjustedAt))
    return parts.join(' · ')
  })
</script>

<template>
  <div class="flex items-center gap-1.5 pt-1.5 text-xs text-muted-foreground" :data-clo-gain-output="output.status">
    <TooltipProvider v-if="output.status !== 'frozen'">
      <Tooltip>
        <TooltipTrigger as-child>
          <Info
            :aria-label="t('assets.configuration.cloGain.hint')"
            class="size-3.5 shrink-0 cursor-help"
            data-clo-gain-hint
          />
        </TooltipTrigger>
        <TooltipContent side="top">
          <p class="text-white">{{ t('assets.configuration.cloGain.hint') }}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
    <p v-if="output.status === 'adjusted'">
      <i18n-t keypath="assets.configuration.cloGain.current" scope="global" tag="span">
        <template #output>
          <b class="font-semibold text-foreground">{{ formatPercent(output.outputPercent) }}</b>
        </template>
      </i18n-t>
      · {{ details }}
    </p>
    <p v-else-if="output.status === 'frozen'">
      {{ t('assets.configuration.cloGain.frozen', { output: formatPercent(output.outputPercent) }) }}
    </p>
    <p v-else-if="output.status === 'waitingBurnHours'">
      {{ t('assets.configuration.cloGain.waitingBurnHours') }}
    </p>
    <p v-else>{{ t('assets.configuration.cloGain.notAdjusted') }}</p>
  </div>
</template>
